import {
  Component,
  DestroyRef,
  ElementRef,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTooltipModule } from '@angular/material/tooltip';
import { EMPTY, Observable, Subject, Subscription, forkJoin, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, expand, map, switchMap } from 'rxjs/operators';
import { SearchInputComponent } from '../../../../components/inputs/search-input/search-input.component';
import {
  StatusFilterComponent,
  StatusFilterOption,
} from '../../../../components/inputs/status-filter/status-filter.component';
import { DataTableComponent } from '../../../../components/ui/data-table/data-table.component';
import { EmptyStateComponent } from '../../../../components/ui/empty-state/empty-state.component';
import { StatusComponent } from '../../../../components/ui/status/status.component';
import {
  DeliveryReportService,
  GatewayPage,
  GatewayRow,
  GatewayStatusFilter,
  GatewaySummary,
} from '../../../../services/reports/delivery-report.service';
import {
  BreakdownRow,
  GatewayOutcome,
  failureBreakdown,
  formatLatency,
  gatewayOutcome,
  gatewayOutcomeLabel,
  gatewayStatusLabel,
  gatewayTone,
  latencyMs,
} from '../../../../services/reports/report-format';
import {
  DEFAULT_REPORT_PERIOD,
  ReportPeriodDays,
  periodPhrase,
} from '../../../../services/reports/report-period';
import { formatShare } from '../../../../services/message/delivery.service';
import { formatCount, formatWhen } from '../../../../../utils/sms';
import { csvFileName, downloadCsv, toCsv } from '../../../../../utils/csv';

/**
 * Rows per request. The endpoint has no total, so more load on demand.
 *
 * It must stay small. For every request the engagement service pulls
 * max(size × (page + 2), size × 10) rows from the SMS channel service, and
 * its HTTP client keeps Spring's default 256 KB response limit. A gateway
 * row is ~1.5 KB of JSON, so anything past ~150 rows fails with a 500
 * (DataBufferLimitException). 10 per request pulls 100 rows.
 */
const BATCH = 10;
/**
 * Batches 0–8 each pull 100 rows; the tenth would pull 110 and climb from
 * there towards the limit, so the log stops at the newest 90 messages until
 * the backend pages properly.
 */
const MAX_BATCHES = 9;

/** Why the log did not load, in words, with the HTTP status for support. */
function gatewayError(error: unknown): { message: string; hint: string } {
  const status = Number((error as { status?: unknown })?.status ?? 0);
  const code = status ? ` (error ${status})` : '';
  if (status === 0) {
    return { message: 'The server could not be reached.', hint: 'Check your connection, then try again.' };
  }
  if (status === 401) {
    return { message: 'Your session has expired.', hint: `Sign in again to see the gateway log${code}.` };
  }
  if (status === 403) {
    return { message: 'Your account cannot read the gateway log.', hint: `Ask an administrator for access${code}.` };
  }
  if (status === 404) {
    return { message: 'The gateway log is not available on this server.', hint: `The SMS service did not recognise the request${code}.` };
  }
  if (status === 408 || status === 502 || status === 503 || status === 504) {
    return {
      message: 'The SMS gateway did not answer in time.',
      hint: `Try a shorter period, or try again in a minute${code}.`,
    };
  }
  return { message: 'The gateway log could not be loaded.', hint: `The server reported an error${code}. Try again in a minute.` };
}
/** Rows per page of the table. */
const PAGE_SIZE = 25;
/** Paged mode: the failure breakdown reads this many of the newest messages. */
const SAMPLE_SIZE = 500;
/** Paged mode: rows per request when building the download, and the most it holds. */
const EXPORT_PAGE = 500;
const EXPORT_CAP = 20_000;

/** What a load produced: real pages and totals, the old newest-batch log, or an error. */
type LoadResult =
  | { kind: 'paged'; page: GatewayPage; summary: GatewaySummary }
  | { kind: 'legacy'; rows: GatewayRow[] }
  | { kind: 'error'; error: unknown };
/** Groups shown per breakdown list. */
const BREAKDOWN_TOP = 5;

/** One gateway log row, ready for the table. */
interface GatewayView {
  key: string;
  row: GatewayRow;
  destination: string;
  sender: string;
  network: string;
  country: string;
  outcome: GatewayOutcome;
  statusLabel: string;
  tone: string;
  narration: string;
  submitted: string;
  submittedExact: string;
  deliveredExact: string;
  latencyMs: number | null;
  latency: string;
  messageId: string;
  content: string;
  /** Lower-cased text the search runs over. */
  haystack: string;
}

const exactFormat = new Intl.DateTimeFormat('en-GB', { dateStyle: 'medium', timeStyle: 'medium' });

function exactTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : exactFormat.format(date);
}

function text(value: unknown): string {
  return String(value ?? '').trim();
}

/** Letters and digits only, lower case: "DELIVERED" and "Delivered" compare equal. */
function squash(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * The gateway's note under a status, when it adds something. Many networks
 * just echo the status ("DELIVERED" under Delivered); those are dropped.
 */
function usefulNarration(row: GatewayRow, statusLabel: string): string {
  const narration = text(row.deliveryStatusNarration);
  const said = squash(narration);
  if (!said || said === squash(text(row.deliveryStatus)) || said === squash(statusLabel)) return '';
  return narration;
}

function toView(row: GatewayRow, index: number): GatewayView {
  const outcome = gatewayOutcome(row.deliveryStatus);
  const latency = latencyMs(row.createdDate, row.deliveryTimestamp || row.receiptTimeStamp);
  const destination = text(row.destination);
  const sender = text(row.sourceAddress);
  const network = text(row.carrierName) || text(row.operatorName);
  const country = text(row.mobileCountry);
  const messageId = text(row.messageId);
  const content = text(row.messageContent);
  const statusLabel = gatewayStatusLabel(row.deliveryStatus);
  return {
    key: `${row.uuid || messageId || 'row'}-${index}`,
    row,
    destination,
    sender,
    network,
    country,
    outcome,
    statusLabel,
    tone: gatewayTone(outcome),
    narration: usefulNarration(row, statusLabel),
    submitted: formatWhen(row.createdDate),
    submittedExact: exactTime(row.createdDate),
    deliveredExact: exactTime(row.deliveryTimestamp || row.receiptTimeStamp),
    latencyMs: latency,
    latency: outcome === 'delivered' ? formatLatency(latency) : '—',
    messageId,
    content,
    haystack: [destination, sender, network, country, messageId, text(row.clientId), text(row.submitId), text(row.bulkReferenceId), content]
      .join(' ')
      .toLowerCase(),
  };
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

/**
 * The SMS gateway's log of API-submitted messages for the period: outcome
 * figures, where failures cluster by network and country, and a searchable
 * log that opens each row to its IDs for tracing.
 *
 * Two modes. 'paged' uses the engagement service's /sms/reports/page and
 * /summary: real pages, totals for the whole period, and filtering and number
 * search in the gateway's own query. Until that service is deployed those
 * 404, and the page falls back to 'legacy': the old endpoint's newest
 * messages in small batches, with every figure describing what has loaded.
 */
@Component({
  selector: 'gateway-log',
  standalone: true,
  imports: [
    MatTooltipModule,
    SearchInputComponent,
    StatusFilterComponent,
    DataTableComponent,
    EmptyStateComponent,
    StatusComponent,
  ],
  templateUrl: './gateway-log.component.html',
  styleUrls: ['../../messaging-reports/report-table.css', './gateway-log.component.css'],
})
export class GatewayLogComponent implements OnInit, OnChanges {
  private reports = inject(DeliveryReportService);
  private destroyRef = inject(DestroyRef);

  @Input() days: ReportPeriodDays = DEFAULT_REPORT_PERIOD;
  /** Bumped by the page's Refresh button. */
  @Input() reloadKey = 0;

  @ViewChild('logToolbar') private logToolbar?: ElementRef<HTMLElement>;
  @ViewChild('logPanel') private logPanel?: ElementRef<HTMLElement>;

  readonly formatCount = formatCount;
  readonly pageSize = PAGE_SIZE;

  readonly outcomeOptions: StatusFilterOption[] = [
    { value: null, title: 'All statuses', icon: 'list', tone: 'neutral' },
    { value: 'delivered', title: 'Delivered', icon: 'check_circle', tone: 'success' },
    { value: 'failed', title: 'Failed', icon: 'error', tone: 'danger' },
    { value: 'pending', title: 'On the way', icon: 'schedule', tone: 'info' },
    { value: 'unknown', title: 'No status', icon: 'help', tone: 'neutral' },
  ];

  /** Which endpoint the log came from this load (see the class comment). */
  mode: 'paged' | 'legacy' = 'paged';
  /** Paged: the period's outcome counts. */
  summary: GatewaySummary | null = null;
  /** Paged: messages matching the status filter and search, across the period. */
  total = 0;
  /** Paged: a page request is out; the current rows stay on screen. */
  paging = false;
  /** Paged: how many of the newest messages the failure breakdown read. */
  sampleSize = 0;
  /** Paged: the download is being gathered page by page. */
  exporting = false;

  views: GatewayView[] = [];
  filtered: GatewayView[] = [];
  page = 0;
  term = '';
  outcome: GatewayOutcome | null = null;
  expandedKey: string | null = null;
  copiedKey: string | null = null;

  status: 'loading' | 'ready' | 'error' = 'loading';
  /** What went wrong, when status is 'error'. */
  errorText = { message: '', hint: '' };
  loadingMore = false;
  moreFailed = false;
  /** The last batch came back full, so older messages may exist. */
  hasMore = false;
  /** Stopped at MAX_BATCHES with older messages left: the backend's limit, not the end of the log. */
  capped = false;
  private nextBatch = 0;

  notice = '';
  noticeTone: 'ok' | 'bad' = 'ok';

  // Summary of what has loaded
  delivered = 0;
  failed = 0;
  pending = 0;
  medianLatency = '—';
  byNetwork: BreakdownRow[] = [];
  byCountry: BreakdownRow[] = [];

  private load$ = new Subject<void>();
  private search$ = new Subject<string>();
  private pageSub?: Subscription;
  private sampleSub?: Subscription;
  private exportSub?: Subscription;
  /** The in-flight "Load older messages" request, dropped when the log reloads. */
  private moreSub?: Subscription;
  private copiedTimer?: ReturnType<typeof setTimeout>;

  get periodText(): string {
    return periodPhrase(this.days);
  }

  get paged(): boolean {
    return this.mode === 'paged';
  }

  /** Legacy only: part of the period has loaded, so the cards describe what is shown. */
  get partial(): boolean {
    return !this.paged && (this.hasMore || this.capped);
  }

  /** Messages the first card counts: the whole period when paged, what has loaded otherwise. */
  get messageCount(): number {
    return this.paged ? (this.summary?.total ?? 0) : this.views.length;
  }

  get rate(): string {
    const settled = this.messageCount;
    return settled ? formatShare(this.delivered / settled) : '—';
  }

  /** The status filter's choices; the gateway's query cannot pick out rows with no status. */
  get statusOptions(): StatusFilterOption[] {
    return this.paged ? this.outcomeOptions.filter((option) => option.value !== 'unknown') : this.outcomeOptions;
  }

  /** The digits of the search, which is all the gateway's query can match on. */
  get termDigits(): string {
    return this.term.replace(/\D/g, '');
  }

  get searchPlaceholder(): string {
    return this.paged ? 'Search by phone number' : 'Search number, sender, network or message ID';
  }

  get pageRows(): GatewayView[] {
    if (this.paged) return this.views;
    const start = this.page * PAGE_SIZE;
    return this.filtered.slice(start, start + PAGE_SIZE);
  }

  /** Rows matching the filter and search: across the period when paged. */
  get matchCount(): number {
    return this.paged ? this.total : this.filtered.length;
  }

  get lastPage(): number {
    return Math.max(0, Math.ceil(this.matchCount / PAGE_SIZE) - 1);
  }

  get rangeText(): string {
    const shown = this.paged ? this.views.length : this.filtered.length;
    if (!shown) return '0';
    const from = this.page * PAGE_SIZE + 1;
    const to = this.paged ? from + this.views.length - 1 : Math.min(this.filtered.length, from + PAGE_SIZE - 1);
    return `${formatCount(from)}–${formatCount(to)}`;
  }

  get isFiltered(): boolean {
    return this.outcome !== null;
  }

  /** "matching messages loaded so far" after the range in the pager. */
  get pagerNoun(): string {
    const noun = this.matchCount === 1 ? 'message' : 'messages';
    const matching = this.isFiltered || this.term ? 'matching ' : '';
    return `${matching}${noun}${this.hasMore ? ' loaded so far' : ''}`;
  }

  ngOnInit(): void {
    this.load$
      .pipe(
        switchMap(() => {
          // Anything still loading belongs to the log being replaced.
          this.moreSub?.unsubscribe();
          this.moreSub = undefined;
          this.pageSub?.unsubscribe();
          this.sampleSub?.unsubscribe();
          this.loadingMore = false;
          this.paging = false;
          this.status = 'loading';
          this.moreFailed = false;
          return forkJoin({
            page: this.reports.gatewayPage(this.days, 0, PAGE_SIZE, this.serverFilters()),
            summary: this.reports.gatewaySummary(this.days),
          }).pipe(
            map(({ page, summary }): LoadResult => ({ kind: 'paged', page, summary })),
            // Not deployed yet (404), or failing: the old endpoint's newest batch.
            catchError(() => this.legacyFirstBatch())
          );
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((result) => this.applyLoad(result));

    // Paged: number search runs in the gateway's query, a moment after typing stops.
    this.search$
      .pipe(debounceTime(350), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.fetchPage(0));

    this.load$.next();
  }

  private legacyFirstBatch(): Observable<LoadResult> {
    return this.reports.gatewayLog(this.days, 0, BATCH).pipe(
      map((rows): LoadResult => ({ kind: 'legacy', rows })),
      catchError((error: unknown) => of<LoadResult>({ kind: 'error', error }))
    );
  }

  private applyLoad(result: LoadResult): void {
    this.expandedKey = null;
    this.capped = false;
    if (result.kind === 'error') {
      this.mode = 'legacy';
      this.errorText = gatewayError(result.error);
      this.hasMore = false;
      this.setRows([]);
      this.status = 'error';
      return;
    }
    if (result.kind === 'legacy') {
      this.mode = 'legacy';
      this.summary = null;
      this.nextBatch = 1;
      this.hasMore = result.rows.length === BATCH;
      this.setRows(result.rows.map(toView));
      this.status = 'ready';
      return;
    }
    this.mode = 'paged';
    this.hasMore = false;
    this.summary = result.summary;
    this.delivered = result.summary.delivered;
    this.failed = result.summary.failed;
    this.pending = result.summary.pending;
    this.medianLatency = '—';
    this.applyPage(result.page);
    this.status = 'ready';
    this.loadSample();
  }

  /** Paged: one page of the period for the current filter and search. */
  private fetchPage(index: number): void {
    if (!this.paged || this.status !== 'ready') return;
    this.paging = true;
    this.pageSub?.unsubscribe();
    this.pageSub = this.reports
      .gatewayPage(this.days, index, PAGE_SIZE, this.serverFilters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          this.paging = false;
          this.expandedKey = null;
          this.applyPage(page);
        },
        error: (error: unknown) => {
          this.paging = false;
          const why = gatewayError(error);
          this.say(`${why.message} ${why.hint}`, 'bad');
        },
      });
  }

  private applyPage(page: GatewayPage): void {
    this.page = page.page;
    this.total = page.total;
    const offset = page.page * PAGE_SIZE;
    this.views = page.items.map((row, i) => toView(row, offset + i));
    this.filtered = this.views;
  }

  private serverFilters(): { status: GatewayStatusFilter | null; search: string } {
    const status = this.outcome === 'unknown' ? null : (this.outcome as GatewayStatusFilter | null);
    return { status, search: this.term };
  }

  /** Paged: where failures cluster, read from the newest messages of the period. */
  private loadSample(): void {
    this.sampleSub?.unsubscribe();
    this.byNetwork = [];
    this.byCountry = [];
    this.sampleSize = 0;
    if (!this.summary?.failed) return;
    this.sampleSub = this.reports
      .gatewayPage(this.days, 0, SAMPLE_SIZE)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (page) => {
          const views = page.items.map(toView);
          this.sampleSize = views.length;
          this.setBreakdown(views);
        },
        // The breakdown is extra; the log and figures stand without it.
        error: () => {},
      });
  }

  private setBreakdown(views: GatewayView[]): void {
    const isFailed = (view: GatewayView) => view.outcome === 'failed';
    this.byNetwork = failureBreakdown(views, (v) => v.network, isFailed)
      .filter((group) => group.failed > 0)
      .slice(0, BREAKDOWN_TOP);
    this.byCountry = failureBreakdown(views, (v) => v.country, isFailed)
      .filter((group) => group.failed > 0)
      .slice(0, BREAKDOWN_TOP);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const days = changes['days'];
    const reload = changes['reloadKey'];
    if ((days && !days.firstChange) || (reload && !reload.firstChange)) {
      this.notice = '';
      this.load$.next();
    }
  }

  reload(): void {
    this.load$.next();
  }

  /** The next batch of older messages. */
  loadMore(): void {
    if (this.loadingMore || !this.hasMore) return;
    this.loadingMore = true;
    this.moreFailed = false;
    const batch = this.nextBatch;
    this.moreSub = this.reports
      .gatewayLog(this.days, batch, BATCH)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (rows) => {
          this.loadingMore = false;
          this.nextBatch = batch + 1;
          const full = rows.length === BATCH;
          this.capped = full && this.nextBatch >= MAX_BATCHES;
          this.hasMore = full && !this.capped;
          const offset = this.views.length;
          this.setRows([...this.views, ...rows.map((row, i) => toView(row, offset + i))], true);
        },
        error: () => {
          this.loadingMore = false;
          this.moreFailed = true;
        },
      });
  }

  onSearch(term: string): void {
    this.term = term.trim();
    if (this.paged) this.search$.next(this.term.replace(/\D/g, ''));
    else this.applyFilters();
  }

  onOutcome(value: string | null): void {
    this.outcome = (value as GatewayOutcome | null) ?? null;
    if (this.paged) this.fetchPage(0);
    else this.applyFilters();
  }

  clearFilters(): void {
    this.onOutcome(null);
  }

  /**
   * Show only failures, from the summary, and bring the filtered log into
   * view. The button hides itself once the filter applies, so focus moves to
   * the log.
   */
  showFailures(): void {
    this.onOutcome('failed');
    const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.logToolbar?.nativeElement.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
    this.logPanel?.nativeElement.focus({ preventScroll: true });
  }

  prevPage(): void {
    if (this.page <= 0) return;
    if (this.paged) this.fetchPage(this.page - 1);
    else this.page -= 1;
  }

  nextPage(): void {
    if (this.page >= this.lastPage) return;
    if (this.paged) this.fetchPage(this.page + 1);
    else this.page += 1;
  }

  toggle(view: GatewayView): void {
    this.expandedKey = this.expandedKey === view.key ? null : view.key;
  }

  /** Enter on the row opens it; Enter on a row button stays with the button. */
  toggleFromKeyboard(event: Event, view: GatewayView): void {
    if (event.target !== event.currentTarget) return;
    event.preventDefault();
    this.toggle(view);
  }

  copyId(view: GatewayView): void {
    if (!view.messageId || typeof navigator === 'undefined' || !navigator.clipboard) {
      this.say('Copying is not available in this browser.', 'bad');
      return;
    }
    navigator.clipboard.writeText(view.messageId).then(
      () => {
        this.copiedKey = view.key;
        clearTimeout(this.copiedTimer);
        this.copiedTimer = setTimeout(() => (this.copiedKey = null), 1600);
      },
      () => this.say('The message ID could not be copied. Open the row and select it instead.', 'bad')
    );
  }

  /**
   * The messages that match the search and filter, as a CSV file: every one
   * in the period when paged (gathered page by page), the loaded ones otherwise.
   */
  downloadCsv(): void {
    if (this.paged) {
      this.exportPeriod();
      return;
    }
    const rows = this.filtered;
    if (!rows.length) {
      this.say('There are no messages to download for this search and filter.', 'ok');
      return;
    }
    this.saveCsv(rows);
    const count = `${formatCount(rows.length)} ${rows.length === 1 ? 'message' : 'messages'}`;
    this.say(
      this.hasMore
        ? `Downloaded ${count}. The file holds the messages loaded here; load older messages first to include them.`
        : `Downloaded ${count}.`,
      'ok'
    );
  }

  private exportPeriod(): void {
    if (this.exporting) return;
    if (!this.total) {
      this.say('There are no messages to download for this search and filter.', 'ok');
      return;
    }
    this.exporting = true;
    this.say(`Preparing ${formatCount(Math.min(this.total, EXPORT_CAP))} messages…`, 'ok');
    const filters = this.serverFilters();
    const rows: GatewayRow[] = [];
    const fetch = (index: number) => this.reports.gatewayPage(this.days, index, EXPORT_PAGE, filters);
    this.exportSub?.unsubscribe();
    this.exportSub = fetch(0)
      .pipe(
        expand((page) => {
          rows.push(...page.items);
          const more = page.items.length > 0 && rows.length < page.total && rows.length < EXPORT_CAP;
          return more ? fetch(page.page + 1) : EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => {
          this.exporting = false;
          const kept = rows.slice(0, EXPORT_CAP);
          if (!kept.length) {
            this.say('There are no messages to download for this search and filter.', 'ok');
            return;
          }
          this.saveCsv(kept.map(toView));
          const count = `${formatCount(kept.length)} ${kept.length === 1 ? 'message' : 'messages'}`;
          this.say(
            this.total > kept.length
              ? `Downloaded the newest ${count} of ${formatCount(this.total)}. Pick a shorter period for the rest.`
              : `Downloaded ${count}.`,
            'ok'
          );
        },
        error: (error: unknown) => {
          this.exporting = false;
          const why = gatewayError(error);
          this.say(`The download stopped: ${why.message} ${why.hint}`, 'bad');
        },
      });
  }

  private saveCsv(rows: GatewayView[]): void {
    const csv = toCsv(rows, [
      { label: 'Submitted at', value: (v) => v.row.createdDate ?? '' },
      { label: 'Destination', value: (v) => v.destination },
      { label: 'Sender', value: (v) => v.sender },
      { label: 'Network', value: (v) => v.network },
      { label: 'Country', value: (v) => v.country },
      { label: 'Status', value: (v) => v.row.deliveryStatus ?? '' },
      { label: 'Outcome', value: (v) => this.outcomeLabel(v.outcome) },
      // The file keeps the gateway's note as sent, echoes included.
      { label: 'Narration', value: (v) => text(v.row.deliveryStatusNarration) },
      { label: 'Delivered at', value: (v) => v.row.deliveryTimestamp || v.row.receiptTimeStamp || '' },
      { label: 'Seconds to delivery', value: (v) => (v.latencyMs === null ? '' : Math.round(v.latencyMs / 1000)) },
      { label: 'Message ID', value: (v) => v.messageId },
      { label: 'Submit ID', value: (v) => v.row.submitId ?? '' },
      { label: 'Receipt ID', value: (v) => v.row.receiptId ?? '' },
      { label: 'Client ID', value: (v) => v.row.clientId ?? '' },
      { label: 'Bulk reference', value: (v) => v.row.bulkReferenceId ?? '' },
      { label: 'Message', value: (v) => v.content },
    ]);
    downloadCsv(csvFileName('api-traffic', `last-${this.days}-days`, this.outcome ?? '', this.term), csv);
  }

  outcomeLabel(outcome: GatewayOutcome): string {
    return gatewayOutcomeLabel(outcome);
  }

  share(row: BreakdownRow): string {
    return formatShare(row.share);
  }

  private setRows(views: GatewayView[], keepPage = false): void {
    this.views = views;
    let delivered = 0;
    let failed = 0;
    let pending = 0;
    const latencies: number[] = [];
    for (const view of views) {
      if (view.outcome === 'delivered') {
        delivered += 1;
        if (view.latencyMs !== null) latencies.push(view.latencyMs);
      } else if (view.outcome === 'failed') failed += 1;
      else if (view.outcome === 'pending') pending += 1;
    }
    this.delivered = delivered;
    this.failed = failed;
    this.pending = pending;
    this.medianLatency = formatLatency(median(latencies));

    this.setBreakdown(views);

    this.applyFilters(keepPage);
  }

  private applyFilters(keepPage = false): void {
    const term = this.term.toLowerCase();
    const digits = term.replace(/[\s()+-]/g, '');
    const numeric = /^\d+$/.test(digits);
    this.filtered = this.views.filter((view) => {
      if (this.outcome && view.outcome !== this.outcome) return false;
      if (!term) return true;
      return view.haystack.includes(term) || (numeric && view.haystack.includes(digits));
    });
    if (!keepPage) this.page = 0;
    else this.page = Math.min(this.page, this.lastPage);
  }

  private say(message: string, tone: 'ok' | 'bad'): void {
    this.notice = message;
    this.noticeTone = tone;
  }
}
